import { PUBLIC_URL } from "@/lib/publicUrl";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl, thumbUrl } from "@/lib/api";
import { colorSwatch } from "@/lib/colors";
import { FavoriteButton } from "@/components/FavoriteButton";
import { Layers, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";

function ShareButton({ id, name }) {
  const share = async () => {
    const url = `${PUBLIC_URL}/collections/${id}`;
    const img = `${process.env.REACT_APP_BACKEND_URL}/api/collections/${id}/share.png`;
    try {
      if (navigator.share) {
        let files;
        try { const b = await (await fetch(img)).blob(); const f = new File([b], `${name}.png`, { type: "image/png" }); if (navigator.canShare?.({ files: [f] })) files = [f]; } catch { /* image optional */ }
        await navigator.share({ title: `${name} — All-Ways Graphic`, text: `Check out the ${name} collection`, url, ...(files ? { files } : {}) });
      } else { await navigator.clipboard.writeText(url); toast.success("Link copied — paste it anywhere"); }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Could not share"); }
  };
  return <button onClick={share} data-testid="collection-share-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-300 hover:text-white border border-zinc-700 hover:border-[#22c3d6] rounded-full px-4 h-9 transition-colors"><Share2 size={15} />Share</button>;
}

export default function CollectionStore() {
  const { collectionId } = useParams();
  return collectionId ? <CollectionDetail id={collectionId} /> : <CollectionList />;
}

function CollectionList() {
  const { data: colls = [], isLoading } = useQuery({ queryKey: ["pub-collections"], queryFn: async () => (await api.get("/collections")).data });
  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="collections-store">
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white flex items-center gap-3"><Layers className="text-[#ff6a13]" size={36} />Collections</h1>
      <p className="text-zinc-400 mt-2">Shop our curated seasonal drops.</p>
      {isLoading && <p className="text-zinc-500 font-mono-p mt-8">Loading…</p>}
      {!isLoading && colls.length === 0 && <p className="text-zinc-500 font-mono-p mt-8" data-testid="collections-empty">No collections are live right now — check back soon.</p>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {colls.map((c) => (
          <Link key={c.collection_id} to={`/collections/${c.collection_id}`} data-testid={`collection-card-${c.collection_id}`}
            className="group bg-[#12141a] border border-zinc-800 rounded-2xl overflow-hidden hover:border-[#ff6a13] transition-colors">
            {(() => { const cover = c.image_url ? fileUrl(c.image_url) : null; const previews = (c.preview_urls || []).slice(0, 4);
              return cover ? <img src={cover} alt={c.name} className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                : previews.length > 0 ? (
                  <div className={`grid ${previews.length >= 4 ? "grid-cols-2 grid-rows-2" : previews.length >= 2 ? "grid-cols-2" : "grid-cols-1"} h-40 gap-0.5 bg-zinc-900`} data-testid={`collection-mosaic-${c.collection_id}`}>
                    {previews.map((u, i) => <div key={i} className="checker flex items-center justify-center overflow-hidden p-2"><img src={thumbUrl(u, 240)} loading="lazy" alt="" className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" /></div>)}
                  </div>
                ) : <div className="w-full h-40 bg-gradient-to-br from-[#1a1d24] to-[#0c0d10]" />; })()}
            <div className="p-6">
              <h2 className="font-display text-2xl font-bold text-white group-hover:text-[#ff6a13] transition-colors">{c.name}</h2>
              <p className="text-zinc-500 font-mono-p text-sm mt-1">{c.item_count} item{c.item_count === 1 ? "" : "s"}</p>
              <span className="inline-flex items-center gap-1 text-[#ff6a13] text-sm font-semibold mt-4">Shop now <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CollectionDetail({ id }) {
  const { data, isLoading, isError } = useQuery({ queryKey: ["pub-collection", id], queryFn: async () => (await api.get(`/collections/${id}`)).data });
  if (isLoading) return <p className="text-zinc-500 font-mono-p max-w-6xl mx-auto px-4 py-12">Loading…</p>;
  if (isError || !data) return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <p className="text-zinc-400" data-testid="collection-not-found">This collection isn't available.</p>
      <Link to="/collections" className="text-[#ff6a13] font-semibold mt-3 inline-block">← All collections</Link>
    </div>
  );
  const products = data.products || [];
  const designs = data.designs || [];
  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="collection-detail">
      <Link to="/collections" className="text-zinc-500 hover:text-white text-sm font-mono-p">← All collections</Link>
      {data.image_url && (data.cover_is_design
        ? <div className="checker w-full h-48 sm:h-64 rounded-2xl mt-3 flex items-center justify-center p-4 border border-zinc-800" data-testid="collection-hero-image"><img src={thumbUrl(data.image_url, 800)} alt={data.name} className="max-h-full max-w-full object-contain" /></div>
        : <img src={thumbUrl(data.image_url, 800)} alt={data.name} className="w-full h-48 sm:h-64 object-cover rounded-2xl mt-3" data-testid="collection-hero-image" />)}
      <div className="flex items-center justify-between gap-3 mt-3">
        <h1 className="font-display text-4xl sm:text-5xl font-black text-white">{data.name}</h1>
        <ShareButton id={id} name={data.name} />
      </div>
      {products.length === 0 && designs.length === 0 && <p className="text-zinc-500 font-mono-p mt-8">No items in this collection yet.</p>}

      {designs.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-bold text-zinc-300 mb-4">Designs</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {designs.map((d) => (
              <Link key={d.design_id} to={`/catalog/${d.design_id}`} data-testid={`collection-design-${d.design_id}`}
                className="relative group bg-[#12141a] border border-zinc-800 rounded-2xl overflow-hidden hover:border-[#ff6a13] transition-colors">
                <FavoriteButton designId={d.design_id} className="absolute top-2 right-2 z-10" size={14} />
                <div className="checker aspect-square flex items-center justify-center p-4"><img src={thumbUrl(d.image_url, 480)} alt={d.title} loading="lazy" className="max-w-full max-h-full object-contain" /></div>
                <div className="p-4">
                  <h3 className="font-semibold text-white truncate">{d.title}</h3>
                  <p className="text-[#10b981] font-mono-p text-sm mt-1">From ${(d.base_price || 0).toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {products.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-bold text-zinc-300 mb-4">Merchandise</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <Link key={p.product_id} to="/merch" data-testid={`collection-product-${p.product_id}`}
                className="group bg-[#12141a] border border-zinc-800 rounded-2xl overflow-hidden hover:border-[#ff6a13] transition-colors">
                {p.image_url && <img src={thumbUrl(p.image_url, 480)} loading="lazy" alt={p.name} className="w-full aspect-square object-cover" />}
                <div className="p-4">
                  <h3 className="font-semibold text-white">{p.name}</h3>
                  <p className="text-[#10b981] font-mono-p text-sm mt-1">From ${(p.base_price || 0).toFixed(2)}</p>
                  <div className="flex gap-1.5 mt-3">
                    {(p.colors || []).filter((c) => !(p.hidden_colors || []).includes(c)).map((c) => (
                      <span key={c} title={c} className="w-5 h-5 rounded-full border border-zinc-700" style={{ backgroundColor: (p.color_hex || {})[c] || colorSwatch(c) }} />
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
